import {FC, useState, useEffect, ChangeEvent} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Button} from '../../../components/Button/Button'
import {Input} from '../../../components/Input/Input'
import Swal from 'sweetalert2'
import {POST, PUT, GET} from '../../../services/api'
import {Loading} from '../../../components/Loading/Loading'
import {useNavigate, useParams} from 'react-router-dom'
import {i18n} from '../../../translate/i18n'
import {BreadCrumbs} from '../../../components/BreadCrumbs/breadCrumbrs'
import uuid from 'react-uuid'
import {HotelExperienceType, getTypeLabel, UpdateHotelExperienceDto} from './types'
import {HospitalityTabs} from '../../../components/HospitalityTabs/HospitalityTabs'
import {EditorQuill} from '../../../components/EditorQuill/EditorQuill'
import {TitleFreePriceInput} from '../Comuns'

const UpdateHotelExperienceContent: FC = () => {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('basic')
  const {id} = useParams<{id: string}>()
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    titleFreePrice: '',
    duration: '',
    includes: [''],
    type: HotelExperienceType.ROMANCE,
    rating: '',
    status: true,
  })
  const [image, setImage] = useState<File | null | string>(null)
  const [currentImage, setCurrentImage] = useState<string>('')

  // Carregar dados da experiência
  useEffect(() => {
    const getExperience = async () => {
      try {
        setLoading(true)
        const res = await GET(`/hotel-experiences/${id}`)
        const experience = res.data

        setFormData({
          title: experience.title || '',
          description: experience.description || '',
          price: experience.price?.toString() || '',
          titleFreePrice: experience.titleFreePrice || '',
          duration: experience.duration || '',
          includes: experience.includes || [''],
          type: experience.type || HotelExperienceType.ROMANCE,
          rating: experience.rating?.toString() || '',
          status: experience.status !== false,
        })
        setCurrentImage(experience.image || '')
        setImage(experience.image || null)
        setLoading(false)
      } catch (e) {
        setLoading(false)
        console.log(e)
        Swal.fire({
          icon: 'error',
          title: String(i18n.t('hospitality_experiences.msg_error_load')),
          text: String(i18n.t('hospitality_experiences.msg_error_load_text')),
        }).then(() => navigate('/hospitality/hotel-experiences'))
      }
    }

    if (id) {
      getExperience()
    }
  }, [id, navigate])

  const handleInputChange = (
    field: string,
    value: string | HotelExperienceType | boolean | number
  ) => {
    setFormData((prev) => {
      const updated = {
        ...prev,
        [field]: value,
      } as any

      // Se o campo alterado for 'price', limpar titleFreePrice quando preço for maior que 0
      if (field === 'price') {
        const num = Number(String(value))
        if (Number.isFinite(num) && num > 0) {
          updated.titleFreePrice = ''
        }
      }

      return updated
    })
  }

  const handleIncludesChange = (index: number, value: string) => {
    const newIncludes = [...formData.includes]
    newIncludes[index] = value
    setFormData({
      ...formData,
      includes: newIncludes,
    })
  }

  const addIncludeItem = () => {
    setFormData({
      ...formData,
      includes: [...formData.includes, ''],
    })
  }

  const removeIncludeItem = (index: number) => {
    if (formData.includes.length > 1) {
      const newIncludes = formData.includes.filter((_, i) => i !== index)
      setFormData({
        ...formData,
        includes: newIncludes,
      })
    }
  }

  const validateForm = () => {
    if (!formData.title.trim()) {
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('hospitality_experiences.msg_error_update')),
        text: String(i18n.t('hospitality_experiences.msg_error_required_title')),
      })
      return false
    }

    if (formData.title.length > 255) {
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('hospitality_experiences.msg_error_update')),
        text: String(i18n.t('hospitality_experiences.msg_error_title_length')),
      })
      return false
    }

    if (!formData.description.trim()) {
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('hospitality_experiences.msg_error_update')),
        text: String(i18n.t('hospitality_experiences.msg_error_required_description')),
      })
      return false
    }

    if (!formData.price.trim()) {
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('hospitality_experiences.msg_error_update')),
        text: String(i18n.t('hospitality_experiences.msg_error_required_price')),
      })
      return false
    }

    if (parseFloat(formData.price) < 0) {
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('hospitality_experiences.msg_error_update')),
        text: String(i18n.t('hospitality_experiences.msg_error_price_negative')),
      })
      return false
    }

    if (!formData.duration.trim()) {
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('hospitality_experiences.msg_error_update')),
        text: String(i18n.t('hospitality_experiences.msg_error_required_duration')),
      })
      return false
    }

    if (formData.duration.length > 100) {
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('hospitality_experiences.msg_error_update')),
        text: String(i18n.t('hospitality_experiences.msg_error_duration_length')),
      })
      return false
    }

    const nonEmptyIncludes = formData.includes.filter((item) => item.trim())
    if (nonEmptyIncludes.length === 0) {
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('hospitality_experiences.msg_error_update')),
        text: String(i18n.t('hospitality_experiences.msg_error_required_includes')),
      })
      return false
    }

    if (formData.rating && (parseFloat(formData.rating) < 0 || parseFloat(formData.rating) > 5)) {
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('hospitality_experiences.msg_error_update')),
        text: String(i18n.t('hospitality_experiences.msg_error_rating_range')),
      })
      return false
    }

    return true
  }

  const updateExperience = async () => {
    if (!validateForm()) return

    try {
      setLoading(true)

      let imageRes = {
        success: false,
        image: currentImage,
      }

      if (image !== currentImage && image instanceof File) {
        if (currentImage) {
          await POST('/upload-images/delete-one', {
            url: currentImage,
          })
        }

        const formData = new FormData()
        formData.append('file', image)
        formData.append('nameImage', `publicity-${uuid()}.jpg`)
        formData.append('originalSize', 'true')
        imageRes = await POST('/upload-images/upload-one', formData)
      }

      const payload: UpdateHotelExperienceDto = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        price: parseFloat(formData.price),
        titleFreePrice: formData.titleFreePrice || null,
        duration: formData.duration.trim(),
        includes: formData.includes.filter((item) => item.trim()),
        type: formData.type,
        rating: formData.rating ? parseFloat(formData.rating) : undefined,
        image: imageRes.success ? imageRes.image : typeof image === 'string' ? image : undefined,
        status: formData.status,
      }

      const response = await PUT(`/hotel-experiences/${id}`, payload)

      if (response.success) {
        Swal.fire({
          icon: 'success',
          title: String(i18n.t('hospitality_experiences.btn_update')),
          text: String(i18n.t('hospitality_experiences.msg_success_update')),
        }).then(() => {
          navigate('/hospitality/hotel-experiences')
        })
      } else {
        throw new Error(response.message || i18n.t('hospitality_experiences.msg_error_update'))
      }

      setLoading(false)
    } catch (e: any) {
      setLoading(false)
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('hospitality_experiences.msg_error_update')),
        text: String(e.message || i18n.t('hospitality_experiences.msg_error_update')),
      })
      console.log(e)
    }
  }

  const renderBasicTab = () => (
    <>
      <div className='row'>
        <div className='col-lg-8'>
          <div className='col-lg-12'>
            <Input
              label={i18n.t('hospitality_experiences.field_title')}
              inputAttr={{
                type: 'text',
                placeholder: i18n.t('hospitality_experiences.field_title_placeholder'),
                maxLength: 255,
              }}
              name='title'
              value={formData.title}
              change={(value: string) => handleInputChange('title', value)}
            />
          </div>
          <div className='col-lg-12'>
            <EditorQuill
              value={formData.description}
              label={i18n.t('hospitality_experiences.field_description')}
              placeholder={i18n.t('hospitality_experiences.field_description_placeholder')}
              change={(value: string) => handleInputChange('description', value)}
              maxLength={5000} // Limite de 5000 caracteres
            />
          </div>
        </div>

        <div className='col-lg-4'>
          <Input
            label={i18n.t('hospitality_experiences.field_image')}
            inputAttr={{
              type: 'image',
            }}
            name='image'
            value={image}
            change={(value: React.BaseSyntheticEvent) => setImage(value.target.files[0])}
          />
          {currentImage && (
            <div className='mt-2'>
              <small className='text-muted'>Imagem atual: {currentImage}</small>
            </div>
          )}
        </div>
      </div>

      <div className='row'>
        <div className='col-lg-4'>
          <TitleFreePriceInput
            value={formData.titleFreePrice || ''}
            onChange={(value) => handleInputChange('titleFreePrice', value || '')}
            label='Título Para Valor Sem Custo'
            placeholder='Ex:. Cortesia do Hotel'
            maxCaracteres={30}
            disabled={parseFloat(formData.price) !== 0 && formData.price !== ''}
            toolTip={true}
            toolTiptext='Se o preço estiver zerado, este texto poderá ser exibido no WebApp. Se vazio, nada será mostrado.'
            toolTipIcon='info-circle'
          />
        </div>
        <div className='col-lg-4'>
          <Input
            label={i18n.t('hospitality_experiences.field_price')}
            inputAttr={{
              type: 'number',
              placeholder: i18n.t('hospitality_experiences.field_price_placeholder'),
              step: '0.01',
              min: '0',
            }}
            name='price'
            value={formData.price}
            change={(value: string) => handleInputChange('price', value)}
          />
        </div>

        <div className='col-lg-4'>
          <Input
            label={i18n.t('hospitality_experiences.field_duration')}
            inputAttr={{
              type: 'text',
              placeholder: i18n.t('hospitality_experiences.field_duration_placeholder'),
              maxLength: 100,
            }}
            name='duration'
            value={formData.duration}
            change={(value: string) => handleInputChange('duration', value)}
          />
        </div>
        <div className='col-lg-4'>
          <div className='mb-3 mt-6'>
            <label className='form-label'>{i18n.t('hospitality_experiences.field_type')}</label>
            <select
              className='form-select'
              value={formData.type}
              onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                handleInputChange('type', e.target.value as HotelExperienceType)
              }
            >
              {Object.values(HotelExperienceType).map((type) => (
                <option key={type} value={type}>
                  {getTypeLabel(type)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className='row'>
        <div className='col-lg-6'>
          <Input
            label={i18n.t('hospitality_experiences.field_rating')}
            inputAttr={{
              type: 'number',
              placeholder: i18n.t('hospitality_experiences.field_rating_placeholder'),
              step: '0.1',
              min: '0',
              max: '5',
            }}
            name='rating'
            value={formData.rating}
            change={(value: string) => handleInputChange('rating', value)}
          />
        </div>
      </div>

      <div className='row'>
        <div className='col-lg-12'>
          <Input
            inputAttr={{
              type: 'checkbox',
            }}
            label={i18n.t('hospitality_experiences.field_status')}
            name='status'
            checked={formData.status}
            change={() => handleInputChange('status', !formData.status)}
          />
        </div>
      </div>
    </>
  )

  const renderDetailsTab = () => (
    <div className='row'>
      <div className='col-lg-12'>
        <label className='form-label'>{i18n.t('hospitality_experiences.field_includes')}</label>
        {formData.includes.map((include, index) => (
          <div key={index} className='d-flex align-items-center mb-2'>
            <Input
              inputAttr={{
                type: 'text',
                placeholder: i18n.t('hospitality_experiences.field_includes_placeholder'),
              }}
              name={`include-${index}`}
              value={include}
              change={(value: string) => handleIncludesChange(index, value)}
            />
            <Button
              text={i18n.t('hospitality_experiences.btn_remove')}
              color='danger'
              size='small'
              click={() => removeIncludeItem(index)}
              btnClass='ms-2 mt-6'
              disabled={formData.includes.length === 1}
            />
          </div>
        ))}
        <Button
          text={i18n.t('hospitality_experiences.btn_add_item')}
          color='info'
          size='small'
          click={addIncludeItem}
          icon='fas fa-plus'
        />
      </div>
    </div>
  )

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='d-flex justify-content-between flex-wrap'>
            <div style={{flex: 1}}>
              <div className='w-100 card'>
                <div className='card-header py-5'>
                  <h3 className='card-title align-items-start flex-column'>
                    <span className='card-label fw-bolder fs-3 mb-1'>
                      {i18n.t('hospitality_experiences.edit')}
                    </span>
                    <BreadCrumbs />
                    <span className='text-muted mt-1 fw-bold fs-7'>
                      {i18n.t('hospitality_experiences.edit_subtitle')}
                    </span>
                  </h3>
                </div>
                <div className='card-body'>
                  <HospitalityTabs
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                    tabs={[
                      {
                        id: 'basic',
                        label: i18n.t('hospitality_experiences.tab_basic'),
                        icon: 'fas fa-info-circle',
                        content: renderBasicTab(),
                        hasError: false,
                      },
                      {
                        id: 'details',
                        label: i18n.t('hospitality_experiences.tab_details'),
                        icon: 'fas fa-cog',
                        content: renderDetailsTab(),
                        hasError: false,
                      },
                    ]}
                  />
                  <div className='d-flex justify-content-end mt-5'>
                    <Button
                      text={i18n.t('buttons.cancel')}
                      color='danger'
                      size='small'
                      click={() => navigate('/hospitality/hotel-experiences')}
                      btnClass='me-3'
                    />
                    <Button
                      size='small'
                      text={i18n.t('hospitality_experiences.btn_update')}
                      color='primary'
                      click={updateExperience}
                      icon='fas fa-save'
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

const UpdateHotelExperience: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: i18n.t('general.dashboard'), path: 'dashboard', isActive: true},
          {
            title: i18n.t('hospitality_experiences.list_title'),
            path: 'hospitality/hotel-experiences',
            isActive: true,
          },
          {
            title: i18n.t('hospitality_experiences.edit_title'),
            path: 'hospitality/hotel-experiences/editar',
            isActive: true,
          },
        ]}
      >
        {i18n.t('hospitality_experiences.edit_title')}
      </PageTitle>
      <UpdateHotelExperienceContent />
    </>
  )
}

export {UpdateHotelExperience}
